import React, { useEffect, useState } from 'react';
import { Provider } from 'mobx-react';
import { Router, RouterStore } from 'react-router-mobx';
import { BrowserRouter } from 'react-router-dom';
import './style/index.scss';
import { ModalComponent } from './components/modal';
import Routes from './routes';
import RootStore from './common/stores';
import setCssVariables from './hooks/useCssVariable';
import { initLogoutDetector, isLoggedIn } from './common/utils/logoutDetector';
import WebSocketUtil from './common/utils/socketUtil';
import Cookie from './common/utils/cookie';
import { getBaseDomainFromHostname } from 'utils/domainUtil';

const rootStore = new RootStore();
const routerStore = new RouterStore();

const POLLING_INTERVAL = 60 * 1000;
const DEV_IMAGE_DOMAINS = ['88named.com', 'namedhub.com', '1named.com'];

window.modalStore = rootStore.modalStore;
window.usersStore = rootStore.usersStore;

function App() {
  const [store, setStore] = useState(rootStore);

  useEffect(() => {
    const mobileArr = ['iPhone', 'iPod', 'BlackBerry', 'Android', 'Windows CE', 'LG', 'MOT', 'SAMSUNG', 'SonyEricsson'];
    let mobileDevice = '';

    for (let txt in mobileArr) {
      if (navigator.userAgent.match(mobileArr[txt]) != null) {
        mobileDevice = mobileArr[txt];
        break;
      }
    }

    window.reflowOnFocus = () => {
      if (navigator.userAgent.indexOf('chrome') && (mobileDevice === 'iPhone' || mobileDevice === 'iPod')) {
        document.getElementById('for-reflow').focus();
      }
    };
  }, []);

  // Force logout handler (e.g., push payload category=forceLogout)
  useEffect(() => {
    const clearAuth = () => {
      try {
        // common cookies
        document.cookie = 'mb_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
        document.cookie = 'session_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
        document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
      } catch (e) {}
      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } catch (e) {}
    };

    const handler = (payload) => {
      // Skip if WebSocket is already handling force logout
      if (window.forceLogoutInProgress) {
        return;
      }
      
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        if (data && (data.category === 'forceLogout' || data.notificationType === 'forceLogout')) {
          // Set flag to prevent other mechanisms from causing reload
          window.forceLogoutInProgress = true;
          clearAuth();
          window.location.replace('/auth/login');
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    // expose for push/sw postMessage or other bridges
    window.handleNamedForceLogout = handler;

    const messageListener = (event) => {
      if (event && event.data) {
        handler(event.data);
      }
    };
    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
      // cleanup global
      if (window.handleNamedForceLogout === handler) {
        delete window.handleNamedForceLogout;
      }
    };
  }, []);

  // Set CSS variables for icons and images
  useEffect(() => {
    const mainHost = getBaseDomainFromHostname(window.location.hostname, 'nmd.co');
    const isDevelopmentEnv = process.env.REACT_APP_API === 'development';
    const imageHost = isDevelopmentEnv && DEV_IMAGE_DOMAINS.includes(mainHost)
      ? 'nmd.co'
      : mainHost;

    setCssVariables({
      'sport-main-icon': `url('https://image.${imageHost}/portal/new_sp_main_icons.png')`,
      'sport-attend-icon': `url('https://${imageHost}/img/page/attend/sp_attend_v15031117.png')`,
      'img-gwango-center': `url('https://image.${imageHost}/portal/img_gwango_center.png')`,
    });
  }, []);

  useEffect(() => {
    let pollingInterval = null;

    async function checkAndStartPolling() {
      await window.usersStore.getInfo();
      const isAdmin = window.usersStore.data.is_admin === true;
      if (isAdmin) {
        pollingInterval = setInterval(() => {
          window.usersStore.getInfo();
        }, POLLING_INTERVAL);
      }
    }
    checkAndStartPolling();

    // Store polling interval globally so WebSocket can stop it
    window.getInfoPollingInterval = pollingInterval;

    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
      if (window.getInfoPollingInterval === pollingInterval) {
        delete window.getInfoPollingInterval;
      }
    };
  }, []);

  // Initialize logout detector (as fallback mechanism)
  useEffect(() => {
    // Only enable for logged-in users
    if (!isLoggedIn()) {
      return;
    }

    const logoutDetectorCleanup = initLogoutDetector({
      onLogout: () => {
        console.log('Session lost - reloading to login page...');
        // Reload page to show login screen
        window.location.reload();
      },
      intervalMs: 15000 // Check every 15 seconds
    });

    // Store cleanup function globally so WebSocket can stop it
    window.logoutDetectorCleanup = logoutDetectorCleanup;

    // Cleanup on unmount
    return () => {
      if (window.logoutDetectorCleanup) {
        window.logoutDetectorCleanup();
        delete window.logoutDetectorCleanup;
      }
    };
  }, []);

  // Subscribe to WebSocket force logout notification
  useEffect(() => {
    if (!isLoggedIn()) {
      return;
    }

    let notificationSocket = null;

    const subscribeForceLogout = async () => {
      // Wait for user info to be loaded
      if (!window.usersStore.data.mb_id) {
        await window.usersStore.getInfo();
      }

      const mb_id = window.usersStore.data.mb_id || Cookie.getCookie('mb_id');
      let session_id = Cookie.getCookie('PHPSESSID') || Cookie.getCookie('session_id');

      if (!mb_id || !session_id) {
        return;
      }

      // Detect environment
      const host = window.location.host;
      const hostname = window.location.hostname;
      let env = 'production';

      if (host.includes('localhost') || host.includes('dev') || host.includes('docker') || hostname.includes('dev') || hostname.includes('docker')) {
        env = 'dev';
      }
      else {
        env = 'production';
      }

      const privateTopic = `named.notification.${env}.private_${mb_id}`;

      // Handler for force logout message - MUST execute immediately, no delay
      // Note: socketUtil.js also handles force logout, but this handler provides additional processing
      const handleForceLogoutMessage = (message) => {
        try {
          // If already handled by socketUtil, just return
          if (window.forceLogoutInProgress) {
            return;
          }
          
          // Message should already be parsed by socketUtil, but handle both cases
          let messageData = message;
          
          if (typeof message === 'string') {
            try {
              messageData = JSON.parse(message);
            } catch (e) {
              // If parse fails, try to decode URL-encoded string
              try {
                const decoded = decodeURIComponent(message);
                messageData = JSON.parse(decoded);
              } catch (e2) {
                return;
              }
            }
          }

          // Handle nested Message structure (from ToastMessage)
          // Format: {AlertID: "...", Message: "{\"category\":\"forceLogout\",...}"}
          if (messageData && messageData.Message && typeof messageData.Message === 'string') {
            try {
              messageData = JSON.parse(messageData.Message);
            } catch (e) {
              // If nested Message parse fails, check if outer structure has force logout
              if (messageData.AlertID && messageData.AlertID.includes('force_logout')) {
                // Try to extract from outer structure
              } else {
                return;
              }
            }
          }

          // Check for force logout indicators
          const isForceLogout = 
            (messageData && (
              messageData.category === 'forceLogout' || 
              messageData.notificationType === 'forceLogout' ||
              (messageData.AlertID && messageData.AlertID.includes('force_logout'))
            ));

          if (isForceLogout) {
            const currentMbId = window.usersStore.data.mb_id || Cookie.getCookie('mb_id');

            // Check mb_id if present in message
            if (messageData.mb_id) {
              if (!currentMbId || currentMbId !== messageData.mb_id) {
                return;
              }
            }

            // Set flag FIRST to prevent any other reloads (before clearing cookies)
            window.forceLogoutInProgress = true;

            // Stop all mechanisms that could cause reload
            if (window.logoutDetectorCleanup && typeof window.logoutDetectorCleanup.stop === 'function') {
              window.logoutDetectorCleanup.stop();
            }
            
            if (window.getInfoPollingInterval) {
              clearInterval(window.getInfoPollingInterval);
              delete window.getInfoPollingInterval;
            }

            // Use notificationTitle and notificationText from server
            const alertTitle = messageData.notificationTitle || '로그아웃';
            const alertMessage = messageData.notificationText || '관리자에 의해 로그아웃되었습니다.';
            
            // Save to sessionStorage and redirect immediately
            try {
              sessionStorage.setItem('forceLogout', JSON.stringify({
                title: alertTitle,
                message: alertMessage,
                type: messageData.type || 'block'
              }));
            } catch (e) {}
            
            // Clear auth and redirect
            try {
              Cookie.deleteCookie('mb_id');
              Cookie.deleteCookie('session_id');
              Cookie.deleteCookie('auth_token');
              Cookie.deleteCookie('PHPSESSID');
            } catch (e) {}
            try {
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            } catch (e) {}
            
            window.location.replace('/auth/login');
          }
        } catch (e) {
          // Silent fail
        }
      };

      // Create WebSocket connection for notifications
      notificationSocket = new WebSocketUtil([]);

      // Subscribe to private topic (subPrivate will retry if socket not open yet)
      notificationSocket.subPrivate(privateTopic, session_id, handleForceLogoutMessage);
    };

    subscribeForceLogout();

    return () => {
      if (notificationSocket) {
        notificationSocket.close(() => {});
      }
    };
  }, []);

  return (
    <Provider {...store}>
      <Router component={BrowserRouter} routerStore={routerStore}>
        <Routes />
      </Router>
      <ModalComponent />
      <input
        id={'for-reflow'}
        onFocus={(event) => {
          event.currentTarget.blur();
        }}
      />
    </Provider>
  );
}

export default App;
